/**
 * Everything the shell registers in the command, shortcut and search
 * registries: manifest commands and shortcuts, office switching, display
 * preferences, help, and the default patient search provider.
 */
import { useNavigate } from '@tanstack/react-router';
import { getShellPatients } from '@velora/sdk';
import { useEffect, useRef } from 'react';
import { clientFor } from '../api.js';
import { registerCommand, registerSearchProvider } from '../commands/registry.js';
import type { PaletteMode } from '../commands/PaletteHost.js';
import { isPlaceholder } from '../builtin/modules.js';
import type { WebModule } from '../module.js';
import { useOfficeScope } from '../office/OfficeScope.js';
import { setCurrentPatient } from '../patient/current.js';
import { usePrefs } from '../prefs/prefs.js';
import { usePrivacy } from '../privacy/privacy.js';
import { registerShortcut } from '../shortcuts/registry.js';
import { useActiveRoute } from './activeRoute.js';

const hasParams = (path: string) => path.includes('$');

export function ShellBindings({
  modules,
  onPalette,
  onShortcutSheet,
  onOfficeMenu,
}: {
  modules: readonly WebModule[];
  onPalette: (mode: PaletteMode) => void;
  onShortcutSheet: () => void;
  onOfficeMenu: () => void;
}) {
  const navigate = useNavigate();
  const office = useOfficeScope();
  const prefs = usePrefs();
  const privacy = usePrivacy();
  const active = useActiveRoute();

  // Latest callbacks without re-registering on every render.
  const live = useRef({
    navigate,
    onPalette,
    onShortcutSheet,
    onOfficeMenu,
    prefs,
    privacy,
    office,
  });
  live.current = { navigate, onPalette, onShortcutSheet, onOfficeMenu, prefs, privacy, office };

  /* Global keys. */
  useEffect(() => {
    const L = () => live.current;
    const offs = [
      registerShortcut({
        id: 'shell.palette',
        keys: 'mod+k',
        description: 'Open the command palette',
        section: 'Global',
        allowInInputs: true,
        run: () => L().onPalette('all'),
      }),
      registerShortcut({
        id: 'shell.patient-search',
        keys: '/',
        description: 'Search patients',
        section: 'Global',
        run: () => L().onPalette('patients'),
      }),
      registerShortcut({
        id: 'shell.shortcuts',
        keys: '?',
        description: 'Show keyboard shortcuts',
        section: 'Global',
        run: () => L().onShortcutSheet(),
      }),
      registerShortcut({
        id: 'shell.privacy',
        keys: 'shift+p',
        description: 'Privacy mode: blur patient names',
        section: 'Global',
        run: () => L().privacy.toggle(),
      }),
      registerShortcut({
        id: 'shell.office',
        keys: 'l',
        description: 'Switch office',
        section: 'Offices',
        run: () => L().onOfficeMenu(),
      }),
    ];
    return () => offs.forEach((off) => off());
  }, []);

  /* Manifest commands and shortcuts. */
  useEffect(() => {
    const offs: Array<() => void> = [];
    for (const { manifest } of modules) {
      const pathOf = new Map(manifest.routes.map((r) => [r.id, r.path]));
      const iconOf = new Map(manifest.nav.map((n) => [n.routeId, n.icon]));
      const permOf = new Map(manifest.routes.map((r) => [r.id, r.permission]));
      const go = (routeId: string, search?: Record<string, string>) => {
        const path = pathOf.get(routeId);
        if (path && !hasParams(path))
          void live.current.navigate({ to: path, ...(search ? { search: search as never } : {}) });
      };
      for (const c of manifest.commands) {
        const path = c.routeId ? pathOf.get(c.routeId) : undefined;
        if (!path || hasParams(path)) continue; // runtime commands register themselves
        const sc = manifest.shortcuts.find((s) => s.routeId === c.routeId && s.scope === 'global');
        const icon = c.routeId ? iconOf.get(c.routeId) : undefined;
        // A command never offers a page its route forbids.
        const perm = c.permission ?? (c.routeId ? permOf.get(c.routeId) : undefined);
        offs.push(
          registerCommand({
            id: c.id,
            title: c.title,
            group: 'Navigate',
            keywords: [...c.keywords, manifest.title],
            ...(icon ? { icon } : {}),
            ...(sc ? { shortcut: sc.keys } : {}),
            ...(perm ? { permission: perm } : {}),
            weight: 1,
            run: () => go(c.routeId ?? '', c.search),
          }),
        );
      }
      for (const s of manifest.shortcuts) {
        if (s.scope !== 'global') continue;
        offs.push(
          registerShortcut({
            id: `${manifest.id}:${s.keys}`,
            keys: s.keys,
            description: s.description,
            section: s.routeId ? 'Navigation' : manifest.title,
            run: () => {
              if (s.routeId) go(s.routeId);
            },
          }),
        );
      }
    }
    return () => offs.forEach((off) => off());
  }, [modules]);

  /* Route-scoped manifest shortcuts: bound only while that module is open. */
  useEffect(() => {
    if (!active) return undefined;
    const m = modules.find((x) => x.manifest.id === active.moduleId);
    if (!m) return undefined;
    const offs = m.manifest.shortcuts
      .filter((s) => s.scope === 'route')
      .map((s) =>
        registerShortcut({
          id: `${m.manifest.id}:route:${s.keys}`,
          keys: s.keys,
          description: s.description,
          section: 'This screen',
          displayOnly: true,
        }),
      );
    return () => offs.forEach((off) => off());
  }, [active, modules]);

  /* Office switching. */
  useEffect(() => {
    const offs = office.offices.map((o) =>
      registerCommand({
        id: `shell.office.${o.id}`,
        title: `Switch to ${o.name}`,
        group: 'Offices',
        keywords: ['office', 'location', o.shortName],
        icon: 'building-2',
        run: () => live.current.office.setScope({ kind: 'location', locationId: o.id }),
      }),
    );
    if (office.offices.length > 1) {
      offs.push(
        registerCommand({
          id: 'shell.office.all',
          title: 'Show all offices combined',
          group: 'Offices',
          keywords: ['office', 'location', 'every', 'combined', 'compare'],
          icon: 'layers',
          run: () => live.current.office.setScope({ kind: 'all' }),
        }),
      );
    }
    return () => offs.forEach((off) => off());
  }, [office.offices]);

  /* Preferences and help. */
  useEffect(() => {
    const L = () => live.current;
    const offs = [
      registerCommand({
        id: 'shell.theme',
        title: L().prefs.mode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme',
        group: 'Preferences',
        keywords: ['theme', 'dark', 'light', 'appearance', 'mode'],
        icon: L().prefs.mode === 'dark' ? 'sun' : 'moon',
        run: () => L().prefs.setMode(L().prefs.mode === 'dark' ? 'light' : 'dark'),
      }),
      registerCommand({
        id: 'shell.privacy',
        title: L().privacy.on ? 'Turn privacy mode off' : 'Turn privacy mode on',
        group: 'Preferences',
        keywords: ['privacy', 'blur', 'hide', 'names', 'screen'],
        icon: 'eye-off',
        shortcut: 'shift+p',
        run: () => L().privacy.toggle(),
      }),
      registerCommand({
        id: 'shell.nav',
        title: L().prefs.navCollapsed ? 'Expand navigation' : 'Collapse navigation',
        group: 'Preferences',
        keywords: ['sidebar', 'menu', 'nav'],
        icon: 'layout-dashboard',
        run: () => L().prefs.set({ navCollapsed: !L().prefs.navCollapsed }),
      }),
      ...(['comfortable', 'compact', 'classic'] as const).map((d) =>
        registerCommand({
          id: `shell.density.${d}`,
          title: `Use ${d} density`,
          group: 'Preferences',
          keywords: ['density', 'rows', 'spacing', d === 'classic' ? 'dentrix' : d],
          icon: 'list-checks',
          ...(L().prefs.density === d ? { hint: 'Current' } : {}),
          run: () => L().prefs.set({ density: d }),
        }),
      ),
      registerCommand({
        id: 'shell.shortcuts',
        title: 'Show keyboard shortcuts',
        group: 'Help',
        keywords: ['keys', 'keyboard', 'help', 'hotkeys'],
        icon: 'keyboard',
        shortcut: '?',
        run: () => L().onShortcutSheet(),
      }),
    ];
    return () => offs.forEach((off) => off());
  }, [prefs.mode, prefs.navCollapsed, prefs.density, privacy.on]);

  /* Default patient search. Skipped when a real (non-placeholder) patients
     module is installed: that module registers its own `patients` provider. */
  const patientsPath = modules
    .flatMap((m) => m.manifest.routes)
    .find((r) => r.path === '/patients')?.path;
  const patientsModule = modules.find((m) => m.manifest.id === 'patients');
  const realPatients = patientsModule !== undefined && !isPlaceholder(patientsModule);
  useEffect(() => {
    if (realPatients) return undefined;
    return registerSearchProvider({
      id: 'patients',
      heading: 'Patients',
      order: 0,
      patients: true,
      permission: 'patient.view',
      search: async (q, { signal, office: officeParam }) => {
        const res = await getShellPatients(clientFor(officeParam), {
          query: { q, limit: 8 },
          signal,
        });
        return res.hits.map((h) => {
          const name = `${h.lastName}, ${h.firstName}${h.preferredName ? ` (${h.preferredName})` : ''}`;
          const dob = h.birthdate ? formatDob(h.birthdate) : null;
          return {
            id: h.id,
            label: name,
            hint: [h.chartNumber, dob, h.status !== 'active' ? h.status : null]
              .filter(Boolean)
              .join(', '),
            icon: 'user-round',
            phi: true,
            office: h,
            run: () => {
              setCurrentPatient({
                id: h.id,
                label: name,
                chartNumber: h.chartNumber,
                homeLocationId: h.homeLocationId,
              });
              if (patientsPath) {
                void live.current.navigate({
                  to: patientsPath,
                  search: (prev: Record<string, unknown>) => ({ ...prev, patient: h.id }),
                } as never);
              }
            },
          };
        });
      },
    });
  }, [patientsPath, realPatients]);

  return null;
}

function formatDob(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${Number(m)}/${Number(d)}/${y}`;
}
